import Modal from '@components/Modal'
import CurrencySelect from '@components/CurrencySelect'
import { Dropdown } from '@components/Dropdown'
import { useAuth } from '@context/AuthContext'
import { useFiles } from '@hooks/useFiles'
import { useImageUpload } from '@hooks/useImageUpload'
import { FileInput } from '@components/Field'
import { fullMemberName, sortMembersWithCurrentFirst } from '@lib/members'
import { currencySymbol } from '@lib/money'
import {
	EditExpenseSubmission,
	emptyExpenseDraft,
	expenseDraftFrom,
	ExpenseDraft,
	setAmount,
	setCurrencyCode,
	setName,
	setPaidBy,
	setSplitAmount,
	setSplitType,
	SPLIT_TYPE_OPTIONS,
	toggleParticipant,
	toUpdatePayload,
	validateExpenseDraft,
} from '../expenseForm'
import { GetExpenseResponse, GetUserType } from '@orbit/api-types'
import React, { useEffect, useMemo, useState } from 'react'
import {
    AmountInput,
    AmountInputWrapper,
    AmountPrefix,
    Button,
    ButtonGroup,
    FormDescription,
    FormGroup,
    FormInput,
    FormLabel,
    ImageActionButton,
    ImageActions,
    ImageOverlay,
    ImagePlaceholder,
    ImagePreview,
    ImageSection,
    ParticipantCheckbox,
    ParticipantName,
    ParticipantRow,
    ParticipantSplitInput,
    ParticipantsList,
    SplitError,
    SplitSummary,
} from '../AddExpenseModal/AddExpenseModal.styles'
import { Segmented } from '@components/Segmented'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'

interface EditExpenseModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (submission: EditExpenseSubmission) => void
    onDelete: () => void
    expense: GetExpenseResponse
    tripId: string
    members: GetUserType[]
    isSubmitting?: boolean
}

const EditExpenseModal: React.FC<EditExpenseModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    onDelete,
    expense,
    tripId,
    members,
    isSubmitting = false,
}) => {
    const { user } = useAuth()
    const { data: filesData } = useFiles(tripId)

    const existingExpenseImageUrl = useMemo(() => {
        const firstId = expense.fileIds?.[0]
        if (!firstId) return null
        return filesData?.files?.find(f => f.id === firstId)?.url ?? null
    }, [expense.fileIds, filesData])

    const [draft, setDraft] = useState<ExpenseDraft>(() =>
        emptyExpenseDraft({ memberIds: [], paidByUserId: 0, currencyCode: '' })
    )
    const {
        imageFile,
        previewUrl: currentImageUrl,
        clearExisting,
        showOverlay: showImageOverlay,
        setShowOverlay: setShowImageOverlay,
        fileInputRef,
        handleImageSelect,
        handleImageRemove,
    } = useImageUpload({ isOpen, existingImageUrl: existingExpenseImageUrl })

    useEffect(() => {
        if (isOpen && expense) setDraft(expenseDraftFrom(expense))
    }, [isOpen, expense])

    const symbol = currencySymbol(draft.currencyCode)
    const validation = validateExpenseDraft(draft)
    const canSubmit = validation.canSubmit && !isSubmitting

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return
        onSubmit({
            payload: toUpdatePayload(draft),
            image: { file: imageFile, clearExisting, existingFileId: expense.fileIds?.[0] },
        })
    }

    const sortedMembers = sortMembersWithCurrentFirst(members, user?.id)

    const formId = 'edit-expense-form'

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Expense"
            size="large"
            backgroundColor={backgroundOrange}
            onDelete={onDelete}
            footer={
                <ButtonGroup>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" form={formId} variant="primary" disabled={!canSubmit}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </ButtonGroup>
            }
        >
            <form id={formId} onSubmit={handleSubmit}>
                <FormGroup>
                    <FormLabel htmlFor="edit-expense-name">Expense Name</FormLabel>
                    <FormInput
                        id="edit-expense-name"
                        type="text"
                        value={draft.name}
                        onChange={e => setDraft(current => setName(current, e.target.value))}
                        placeholder="e.g., Dinner at the restaurant"
                        maxLength={75}
                        autoFocus
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>Image</FormLabel>
                    <FormDescription>
                        Attach a photo of a receipt, bill, or anything related to the expense
                    </FormDescription>
                    <FileInput
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleImageSelect}
                    />
                    <ImageSection>
                        {currentImageUrl ? (
                            <>
                                <ImagePreview onClick={() => setShowImageOverlay(true)}>
                                    <img src={currentImageUrl} alt="Receipt" />
                                </ImagePreview>
                                <ImageActions>
                                    <ImageActionButton
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        Replace Image
                                    </ImageActionButton>
                                    <ImageActionButton
                                        type="button"
                                        $danger
                                        onClick={handleImageRemove}
                                    >
                                        Delete Image
                                    </ImageActionButton>
                                </ImageActions>
                            </>
                        ) : (
                            <ImagePlaceholder onClick={() => fileInputRef.current?.click()}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                    <polyline points="21 15 16 10 5 21" />
                                </svg>
                            </ImagePlaceholder>
                        )}
                    </ImageSection>
                </FormGroup>

                {showImageOverlay && currentImageUrl && (
                    <ImageOverlay onClick={() => setShowImageOverlay(false)}>
                        <img src={currentImageUrl} alt="Receipt full size" />
                    </ImageOverlay>
                )}

                <FormGroup>
                    <FormLabel htmlFor="edit-expense-amount">Amount</FormLabel>
                    <AmountInputWrapper>
                        <AmountPrefix>{symbol}</AmountPrefix>
                        <AmountInput
                            id="edit-expense-amount"
                            type="number"
                            value={draft.amount}
                            onChange={e => setDraft(current => setAmount(current, e.target.value))}
                            placeholder="0.00"
                            min="0.01"
                            step="0.01"
                        />
                    </AmountInputWrapper>
                </FormGroup>

                <FormGroup>
                    <CurrencySelect
                        value={draft.currencyCode}
                        onChange={code => setDraft(current => setCurrencyCode(current, code))}
                        label="Currency"
                        htmlFor="edit-expense-currency"
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="edit-expense-paid-by">Paid By</FormLabel>
                    <Dropdown
                        id="edit-expense-paid-by"
                        aria-label="Paid By"
                        options={sortedMembers.map(member => ({
                            value: String(member.id),
                            label: fullMemberName(member, user?.id),
                        }))}
                        value={String(draft.paidByUserId)}
                        onChange={value => setDraft(current => setPaidBy(current, Number(value)))}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>Split Type</FormLabel>
                    <Segmented
                        aria-label="Split type"
                        options={SPLIT_TYPE_OPTIONS}
                        value={draft.splitType}
                        onChange={type => setDraft(current => setSplitType(current, type))}
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel>Split Between</FormLabel>
                    <ParticipantsList>
                        {sortedMembers.map(member => {
                            const isSelected = draft.participantIds.includes(member.id)
                            return (
                                <ParticipantRow
                                    key={member.id}
                                    $isSelected={isSelected}
                                    onClick={() => setDraft(current => toggleParticipant(current, member.id))}
                                >
                                    <ParticipantCheckbox $checked={isSelected} />
                                    <Avatar user={member} size="md" />
                                    <ParticipantName>{fullMemberName(member, user?.id)}</ParticipantName>
                                    {isSelected && draft.splitType !== 'Equally' && (
                                        <ParticipantSplitInput
                                            type="number"
                                            placeholder={draft.splitType === 'Percent' ? '%' : '0.00'}
                                            value={draft.splitAmounts[member.id] || ''}
                                            onChange={e =>
                                                setDraft(current =>
                                                    setSplitAmount(current, member.id, e.target.value)
                                                )
                                            }
                                            onClick={e => e.stopPropagation()}
                                            min="0"
                                            step={draft.splitType === 'Percent' ? '1' : '0.01'}
                                        />
                                    )}
                                </ParticipantRow>
                            )
                        })}
                    </ParticipantsList>
                    <SplitSummary>
                        {validation.splitValid ? (
                            validation.splitMessage
                        ) : (
                            <SplitError>{validation.splitMessage}</SplitError>
                        )}
                    </SplitSummary>
                </FormGroup>
            </form>
        </Modal>
    )
}

export default EditExpenseModal
